<template>
  <div
    v-bind="$attrs"
    :class="b(modifiers)"
  >
    <!-- for pimcore form use -->
    <select
      v-bind="$attrs"
      v-model="internalValue"
      :id="$attrs.id || `${$options.name}--${uuid}`"
      ref="nativeSelect"
      :name="name"
      :multiple="multiple"
      :aria-label="label"
      class="invisible"
    >
      <option
        v-for="option in options"
        :key="option.value"
        :value="option.value"
        :disabled="option.disabled"
      >
        {{ option.label }}
      </option>
    </select>
    <div
      :class="b('inner')"
      @click="onOutputClick"
    >
      <div :class="b('content')">
        <div
          v-if="design"
          :class="b('toggle-icon')"
          @click="onToggleIconClick"
        ></div>
        <div :class="b('output')">
          <div
            :class="b('output-inner')"
            :style="outputStyle"
          >
            <span
              v-if="outputValue"
              :class="b('value')"
            >
              <span :class="[b('output-label'), hideLabel ? 'invisible' : null]">
                {{ label }}
              </span>
              {{ outputValue }}
              <span
                v-if="showCount && !isActive && multiple"
                :class="b('count')"
              >
                {{ checkedOptions.length }}
              </span>
            </span>
            <span
              v-else
              :class="b('placeholder')"
            >
              {{ label }}
            </span>
          </div>
        </div>

        <div
          v-if="isActive"
          :class="b('dropdown')"
        >
          <div
            v-if="hasSearch && isActive"
            :class="b('search-wrapper')"
          >
            <e-search
              v-model="searchTerm"
              ref="searchInput"
              :label="label"
              :negative="!negative"
              height="200"
              name="search-field"
            />
          </div>
          <div
            v-else-if="isActive"
            :class="b('inner-label')"
            @click="onToggleIconClick"
          >
            {{ label }}
          </div>
          <div :class="b('options-wrapper')">
            <e-scrollable
              :class="b('scroll')"
              track-visible
            >
              <ul :class="b('options')">
                <li
                  v-if="placeholder"
                  :class="b('option', { placeholder: true })"
                >
                  <component
                    v-model="internalValue"
                    :is="multiple ? 'e-checkbox' : 'e-radio'"
                    :value="0"
                    :disabled="disablePlaceholder"
                    :design="design"
                    name="placeholder"
                    @update:model-value="onCheckboxChange"
                  >
                    <span>{{ placeholder }}</span>
                  </component>
                </li>

                <li
                  v-for="item in filteredOptions"
                  :key="item.id"
                  :class="b('option')"
                >
                  <component
                    v-model="internalValue"
                    :is="multiple ? 'e-checkbox' : 'e-radio'"
                    :value="item[valueField]"
                    :name="nameStub"
                    :design="design"
                    :design-size="designSize"
                    :disabled="item.disabled"
                    @update:model-value="onCheckboxChange"
                  >
                    <span>{{ item[labelField] }}</span>
                  </component>
                </li>

                <li
                  v-if="!filteredOptions.length"
                  :class="b('list-empty')"
                >
                  {{ $t('c-select.optionsEmpty') }}
                </li>
              </ul>
            </e-scrollable>
          </div>

          <div
            v-if="!autoSave"
            :class="b('button-wrapper')"
          >
            <e-button
              :disabled="!isDirty"
              width="full"
              type="button"
              primary
              @click="onSaveClick"
            >
              {{ $t('c-select.save') }}
            </e-button>
          </div>
        </div>
      </div>
    </div>
    <!-- @slot allows to display custom messages -->
    <div
      v-if="$slots.default"
      :class="b('notification')"
    >
      <slot></slot>
    </div>
  </div>
</template>

<script>
  import { CUSTOM_EVENTS_MAP } from '@/setup/globals';
  import useUuid from '@/compositions/uuid.js';
  import eButton from '@/elements/e-button.vue';
  import eScrollable from '@/elements/e-scrollable.vue';
  import eSearch from '@/elements/e-search.vue';
  import propScale from '@/helpers/prop.scale.js';
  import designMixin from '@/mixins/design.js';
  import formStates from '@/mixins/form-states.js';
  import themes from '@/mixins/themes.js';

  /**
   * Custom select component. Supports search, multiselect and auto/manual save.
   *
   * Note: switching between multiple/single select is possible, but you need also to switch the value binding!
   */
  export default {
    name: 'c-select',

    components: {
      eButton,
      eScrollable,
      eSearch,
    },
    mixins: [formStates, themes, designMixin],
    inheritAttrs: false,

    props: {
      /**
       * Array of Strings which is the basis for the options.
       */
      options: {
        type: Array,
        required: true,
      },

      /**
       * Initially selected active values.
       */
      modelValue: {
        type: [String, Number, Array],
        default: null,
      },

      /**
       * Defines the label for the multi-select.
       */
      label: {
        type: String,
        default: '',
      },

      /**
       * Hides the input label visually.
       */
      hideLabel: {
        type: Boolean,
        default: false,
      },

      /**
       * Defines a unique name for the form fields.
       */
      name: {
        type: String,
        default: null,
      },

      /**
       * Allows to apply an inverted style to the component on darker backgrounds.
       */
      negative: {
        type: Boolean,
        default: false,
      },

      /**
       * Defines if the options are searchable.
       */
      hasSearch: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows to change the default field, from which the value is taken for each option.
       */
      valueField: {
        type: String,
        default: 'value',
      },

      /**
       * Allows to change the default field, from which the label text is taken for each option.
       */
      labelField: {
        type: String,
        default: 'label',
      },

      /**
       * Allows to enable/disable support to select multiple values.
       */
      multiple: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows to show a placeholder item.
       */
      placeholder: {
        type: String,
        default: null,
      },

      /**
       * Allows to enable the placeholder option.
       */
      disablePlaceholder: {
        type: Boolean,
        default: true,
      },

      /**
       * Allows to disable the element.
       */
      disabled: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows to change the height of the select element.
       */
      height: propScale(500, [300, 400, 500]),

      /**
       * Allows to show number of selected items (only if multiple).
       */
      showCount: {
        type: Boolean,
        default: true,
      },

      /**
       * Defines if the component should trigger the save process without clicking the save button.
       */
      autoSave: {
        type: Boolean,
        default: true,
      },

      /**
       * Defines if the component is in the formbuilder context.
       */
      formbuilder: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['update:modelValue'],

    setup() {
      return {
        ...useUuid(),
      };
    },

    data() {
      return {
        /**
         * @type {Boolean} Defines if the multi-select has been changed.
         */
        isDirty: false,

        /**
         * @type {Array} The checked options IF multiple.
         */
        checkedOptions: [],

        /**
         * @type {String} The checked item IF NOT multiple.
         */
        checkedItem: null,

        /**
         * @type {String} The search term to filter values.
         */
        searchTerm: '',
      };
    },

    computed: {
      /**
       * Returns a content based width definition for the output element.
       *
       * @returns {Object}
       */
      outputStyle() {
        const labelLength = this.label?.length || 0;
        let length = this.placeholder?.length || 0;

        if (length < labelLength) {
          length = labelLength;
        }

        this.options?.forEach((option) => {
          const optionLength = `${option[this.labelField] || ''}`.length;

          if (length < optionLength) {
            length = optionLength;
          }
        });

        return {
          width: `${length * 0.9}ch`, // In general not all letters are as wide as 0.
        };
      },

      /**
       * Returns a name stub for the options.
       *
       * @returns {String}
       */
      nameStub() {
        return this.name || `${this.$options.name}-${this.uuid}`;
      },

      /**
       * Gets/Sets the value based on the selected mode (multiple/single).
       */
      internalValue: {
        get() {
          return this.multiple ? this.checkedOptions : this.checkedItem;
        },
        set(value) {
          if (this.multiple) {
            this.checkedOptions = value;
          } else {
            this.checkedItem = value;
          }
        },
      },

      /**
       * Returns the active values as an Array.
       *
       * @returns {Array.<String>}
       */
      valueArray() {
        const { modelValue } = this;

        switch (typeof modelValue) {
          case 'object':
            if (Array.isArray(modelValue)) {
              return modelValue;
            }
            break;

          case 'string':
            return modelValue.split(',');

          case 'number':
            return [modelValue];

          // no default
        }

        return [];
      },

      /**
       * Defines state modifier classes.
       *
       * @returns {Object}
       */
      modifiers() {
        const { theme } = this;

        return {
          ...this.stateModifiers,
          ...this.designModifiers,
          negative: this.negative,
          multiple: this.multiple,
          height: this.height,
          labelHidden: this.hideLabel,
          theme,
        };
      },

      /**
       * Gets all the checked options labels as a String separated by ",".
       *
       * @returns {String}
       */
      outputValue() {
        const { options, valueField, labelField } = this;

        if (!options) {
          return null;
        }

        if (this.multiple) {
          return this.checkedOptions
            ?.map((value) => options.find((item) => item[valueField] === value)?.[labelField])
            .filter(Boolean)
            .join(', ');
        }

        return options.find((item) => item[valueField] === this.checkedItem)?.[labelField];
      },

      /**
       * Gets the filtered options.
       *
       * @returns {Array.<Object>}
       */
      filteredOptions() {
        const { options } = this;

        if (this.hasSearch && this.searchTerm) {
          const searchTermLowerCase = this.searchTerm.toLowerCase();

          return options.filter((item) => item[this.labelField]?.toLowerCase().includes(searchTermLowerCase));
        }

        return options;
      },
    },
    watch: {
      /**
       * Observes the valueArray to react on changes from outside.
       */
      valueArray: {
        immediate: true,
        handler() {
          if (this.multiple) {
            this.checkedOptions = this.valueArray;
          } else {
            this.checkedItem = this.valueArray?.[0];
          }
        },
        deep: true,
      },

      /**
       * We need this as the form builder javascript reacts on the change event which is not triggered when the v-model is updated.
       * @see /node_modules/js-pimcore-formbuilder/src/js/components/conditionalLogic.js
       *
       * @param {String} newVal - the new value.
       * @param {String} oldVal - The old value.
       */
      internalValue(newVal, oldVal) {
        if (this.formbuilder && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.$nextTick(() => {
            const event = new Event('change', { bubbles: true });

            this.$refs.nativeSelect.dispatchEvent(event);
          });
        }
      },
    },
    // beforeCreate() {},
    created() {
      window.addEventListener('click', this.onOutsideClick, { capture: true });
      window.addEventListener(CUSTOM_EVENTS_MAP.formbuilderHideField, this.onResetSelect, { capture: true });
    },
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    unmounted() {
      window.removeEventListener('click', this.onOutsideClick);
      window.removeEventListener(CUSTOM_EVENTS_MAP.formbuilderHideField, this.onResetSelect);
    },

    methods: {
      /**
       * Sets isDirty on true, if a checkbox is checked or unchecked.
       */
      onCheckboxChange() {
        this.isDirty = true;

        if (this.autoSave) {
          this.save();
        }
      },

      /**
       * Checks, if the click was outside of the component, and if so, toggles the active state.
       *
       * @param {Event} event - The original click event.
       */
      onOutsideClick(event) {
        if (this.isActive && event.target !== this.$el && !this.$el.contains(event.target)) {
          if (this.multiple && this.isDirty) {
            this.save();
          } else {
            setTimeout(() => {
              // Timeout was needed because of 'capture' on event.
              this.isActive = false;
            });
          }
        }
      },

      /**
       * Sets the active state of the component, if there is a click on the output field.
       */
      onOutputClick() {
        if (!this.isActive && !this.disabled) {
          this.isActive = true;
          this.setFocusOnInput();
        }
      },

      /**
       * Opens or closes the select.
       */
      onToggleIconClick() {
        if (this.isActive) {
          if (this.multiple && this.isDirty) {
            this.save();
          }
          this.isActive = false;
        } else {
          this.onOutputClick();
        }
      },

      /**
       * Calls the update function, if the save button is clicked.
       */
      onSaveClick() {
        this.save(false);
      },

      /**
       * Updates the state of the component. If the checked array changed, it will update the checkedStore
       * and emit the new checked array to the parent component.
       *
       * @param {Boolean} remainOpenAfterSave - Defines if the dropdown should remain open after the save.
       */
      save(remainOpenAfterSave = this.multiple) {
        this.$emit('update:modelValue', this.internalValue);

        this.isActive = remainOpenAfterSave;
        this.isDirty = false;
      },

      /**
       * Sets the focus to the input element of the c-search component.
       */
      setFocusOnInput() {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.$refs.input.focus();
          }
        });
      },

      /**
       * Handles the reset of the select.
       *
       * @param {Event} event - The trigger event.
       */
      onResetSelect(event) {
        if (!this.formbuilder) {
          return;
        }

        const { fieldId } = event?.detail || {};

        if (fieldId === this.$attrs.id) {
          this.internalValue = undefined;
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';
  @use '@/setup/scss-v2/component' as v2;
  @use '@/setup/scss-v2/fontawesome' as v2_fa;

  c-select {
    $height: 56px;
    $radius: 900px; // Force half circles.
    $padding: 12px;
    $border-width: 1px;

    display: block;
    box-sizing: content-box;
    min-height: functions.rem($height - (2 * $padding) + (2 * $border-width));
    padding: functions.rem(($padding - $border-width) ($padding * 3.5) ($padding - $border-width) ($padding * 2.5));
    border-radius: $radius;
    background-color: functions.get-theme-var('c-select-background-color');

    * {
      display: none !important; // stylelint-disable-line declaration-no-important
    }
  }

  .c-select {
    $this: &;
    $radius: 900px; // Force half circles.
    $height: 56px;
    $height400: 48px;
    $height300: 40px;
    $padding: 10px;
    $border-width: 1px;

    position: relative;

    &__inner {
      position: relative;
      display: flex;
      align-items: flex-end;
      cursor: pointer;
    }

    &__content {
      width: 100%;
      border: $border-width solid transparent;
      border-radius: $radius;
      background-color: functions.get-theme-var('c-select-background-color');
      transition: box-shadow variables.$transition-duration--200;
    }

    &__output {
      display: flex;
      justify-content: space-between;
      align-items: center;
      max-width: 100%;
      min-height: functions.rem($height);
      padding: functions.rem(($padding - $border-width) ($padding * 4.5) ($padding - $border-width) ($padding * 3));
      outline: none;
      border: 0;
      border-radius: $radius;
      background: transparent functions.get-theme-var('c-select-dropdown-icon') right #{functions.rem($padding * 2.5)}
        top 55% no-repeat;
      background-size: functions.rem(16px);
      color: variables.$color-grayscale--200;
      line-height: functions.rem($height - (2 * $padding));
      text-overflow: ellipsis;
      appearance: none;
    }

    &__output-inner {
      min-width: 100%;
    }

    &__placeholder,
    &__value {
      @include mixins.font-size(variables.$font-size--16);

      display: block;
      overflow: hidden;
      color: functions.get-theme-var('c-select-value-color');
      line-height: 1.4;
      white-space: nowrap;
      text-overflow: ellipsis;

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
      }
    }

    &__value {
      margin-top: functions.rem(variables.$spacing--10);
      margin-bottom: functions.rem(-1 * variables.$spacing--10);
    }

    &__placeholder {
      color: variables.$color-grayscale--200;
    }

    &__output-label {
      position: absolute;
      top: 0;
      left: functions.rem($padding * 2.5);
      color: variables.$color-grayscale--500;
      font-size: 0.8em;
      line-height: functions.rem($height - (2 * $padding)); // Unifies position between height variants.
    }

    &__inner-label {
      display: flex;
      align-items: center;
      min-height: functions.rem($height);
      padding: functions.rem(($padding - $border-width) ($padding * 3.5) ($padding - $border-width) ($padding * 3));
      cursor: default;
      line-height: functions.rem($height - (2 * $padding));
    }

    &__options-wrapper {
      height: 0;
      padding-bottom: functions.rem(variables.$spacing--5);
      overflow: hidden;
      opacity: 0;
      border-top: 1px solid transparent;
      border-radius: 0 0 #{math.div($height, 2)} #{math.div($height, 2)}; // Cut hover effect of children.
      transition: opacity variables.$transition-duration--300;
    }

    &__scroll {
      max-height: functions.rem(120px);

      @include mixins.media(sm) {
        max-height: functions.rem(225px);
      }
    }

    &__options {
      @extend %list-reset;
      @extend %hyphens;
    }

    &__option {
      color: functions.get-theme-var('c-select-option-color');
      transition: background-color variables.$transition-duration--300;

      &:hover {
        background-color: functions.get-theme-var('c-select-active-option-background-color');
      }

      &--placeholder {
        color: variables.$color-grayscale--200;
      }
    }

    &__list-empty {
      @include mixins.font(variables.$font-size--16);

      display: block;
      padding: functions.rem(variables.$spacing--5 ($padding * 2.5));
      color: variables.$color-grayscale--200;
    }

    &__button-wrapper {
      display: none;
      padding: functions.rem(variables.$spacing--10);
      border-top: 1px solid variables.$color-grayscale--900;
    }

    &__notification {
      @include mixins.font(variables.$font-size--14);

      padding: functions.rem(variables.$spacing--5 (2 * $padding) 0);

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--14, variables.$font-size--lg-scale-basis);
      }

      &:empty {
        display: none;
      }

      #{$this}--state-error & {
        color: variables.$color-status--danger;
      }

      #{$this}--state-warning & {
        color: variables.$color-status--danger;
      }

      #{$this}--state-info & {
        color: variables.$color-status--info;
      }

      #{$this}--state-success & {
        color: variables.$color-status--success;
      }
    }

    .e-radio,
    .e-checkbox {
      padding: functions.rem(variables.$spacing--10 ($padding * 4) variables.$spacing--10 ($padding * 3));
    }

    .e-checkbox__checkbox {
      &::before,
      &::after {
        // Restores fake checkbox position after padding was applied.
        margin: functions.rem(variables.$spacing--10 0 0 ($padding * 2.5));
      }
    }

    .e-radio {
      &__radio {
        display: none;
      }

      &__label-text {
        padding-left: 0;
      }

      &--selected {
        background-color: variables.$color-grayscale--1000;
      }
    }

    .e-search {
      &__label {
        $hPadding: $padding - $border-width;

        padding: functions.rem(
          $hPadding (($padding * 3.5) - $border-width) $hPadding (($padding * 2.5) - $border-width)
        );
        background: transparent;
      }

      &__field {
        @include mixins.font(variables.$font-size--16);
      }
    }

    .e-scrollable {
      .simplebar-track.simplebar-vertical {
        right: functions.rem(variables.$spacing--25);
      }

      .simplebar-content {
        padding: 0 !important; // stylelint-disable-line declaration-no-important
      }
    }

    &--multiple &__options-wrapper {
      border-radius: 0;
    }

    &--active {
      #{$this}__options-wrapper {
        height: auto;
        opacity: 1;
        border-top-color: variables.$color-grayscale--900;
      }

      &#{$this}--multiple #{$this}__options-wrapper {
        padding-bottom: 0;
      }

      #{$this}__dropdown {
        @include mixins.z-index(front);

        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        overflow: hidden;
        border-radius: 20px;
        background-color: variables.$color-grayscale--920;
        box-shadow: 0 3px 6px rgba(variables.$color-grayscale--0, 0.16);
      }

      #{$this}__button-wrapper {
        display: block;

        .e-button {
          // If not applied, the button could overflow the component.
          min-width: auto;
        }
      }
    }

    &__count {
      @include mixins.font(variables.$font-size--12, variables.$line-height--18);

      position: absolute;
      top: functions.rem(-11px);
      right: functions.rem($padding * 2.5);
      padding: functions.rem(2px variables.$spacing--5);
      border-radius: 2px;
      background-color: functions.get-theme-var('c-select-count-background-color');
      color: functions.get-theme-var('c-select-count-color');
    }

    &__hint {
      @include mixins.font(variables.$font-size--12);

      display: block;
      color: functions.get-theme-var('c-select-hint-color');
    }

    &--negative {
      #{$this}__dropdown,
      #{$this}__content {
        background-color: functions.get-theme-var('c-select-background-color--negative');
      }

      .e-radio--selected,
      #{$this}__option:hover {
        background-color: functions.get-theme-var('c-select-active-option-background-color--negative');
      }
    }

    &--disabled {
      #{$this}__inner {
        cursor: default;
      }

      #{$this}__placeholder,
      #{$this}__value,
      #{$this}__output {
        border-color: variables.$color-grayscale--500;
        color: variables.$color-grayscale--500;
      }

      #{$this}__output {
        background-image: functions.get-theme-var('c-select-dropdown-icon--disabled');
      }
    }

    &--height-400 {
      #{$this}__value {
        margin-top: functions.rem(8px);
      }

      #{$this}__output {
        min-height: functions.rem($height400);
        line-height: functions.rem($height400 - (2 * $padding));
      }
    }

    &--height-300 {
      #{$this}__value {
        margin-top: functions.rem(variables.$spacing--5);
      }

      #{$this}__output {
        min-height: functions.rem($height300);
        line-height: functions.rem($height300 - (2 * $padding));
      }
    }

    &--label-hidden {
      #{$this}__value {
        margin: 0;
      }
    }

    &--state-error {
      #{$this}__content {
        border-color: variables.$color-status--danger;
      }
    }
  }

  c-select,
  .c-select {
    $this: &;

    &--theme-sagemax {
      border-radius: 0;

      #{$this}__content,
      #{$this}__output,
      #{$this}__dropdown,
      #{$this}__options-wrapper {
        border-radius: 0;
      }

      #{$this}--active #{$this}__dropdown {
        border-radius: 0;
      }
    }
  }

  //
  // Design V2
  //
  @include v2.define-theme-properties-for(
    'default',
    'c-select',
    (
      'padding-h--large': var(--e-input-padding-h--large),
      'padding-h--medium': var(--e-input-padding-h--medium),
      'padding-h--small': var(--e-input-padding-h--small),
      'padding-v--large': var(--e-input-padding-v--large),
      'padding-v--medium': var(--e-input-padding-v--medium),
      'padding-v--small': var(--e-input-padding-v--small),
      'background': var(--e-input-background),
      'background--focus': var(--e-input-background--focus),
      'background--hover': var(--e-input-background--hover),
      'background--disabled': var(--e-input-background--disabled),
      'option-padding-v--large': 14px,
      'option-padding-v--medium': 12px,
      'option-padding-v--small': 8px,
      'field-height--large': var(--e-input-field-height--large),
      'field-height--medium': var(--e-input-field-height--medium),
      'field-height--small': var(--e-input-field-height--small),
      'field-color': var(--e-input-field-color),
      'field-color--disabled': var(--e-input-field-color--disabled),
      'border-width': var(--e-input-border-width),
      'border-radius': var(--e-input-border-radius),
      'border-color': var(--e-input-border-color),
      'border-color--disabled': var(--e-input-border-color--disabled),
      'border-color--error': var(--e-input-border-color--error),
      'border-color--warning': var(--e-input-border-color--warning),
      'border-color--success': var(--e-input-border-color--success),
      'border-color--info': var(--e-input-border-color--info),
      'notification-color': var(--e-input-notification-color),
      'notification-color--disabled': var(--e-input-notification-color--disabled),
      'notification-color--error': var(--e-input-notification-color--error),
      'notification-color--warning': var(--e-input-notification-color--warning),
      'notification-color--success': var(--e-input-notification-color--success),
      'notification-color--info': var(--e-input-notification-color--info),
    )
  );

  .c-select {
    $this: &;

    &--design-size-large {
      --padding-h: var(--c-select-padding-h--large);
      --padding-v: var(--c-select-padding-v--large);
      --option-padding-v: var(--c-select-option-padding-v--large);
      --field-height: var(--c-select-field-height--large);
    }

    &--design-size-medium {
      --padding-h: var(--c-select-padding-h--medium);
      --padding-v: var(--c-select-padding-v--medium);
      --option-padding-v: var(--c-select-option-padding-v--medium);
      --field-height: var(--c-select-field-height--medium);
    }

    &--design-size-small {
      --padding-h: var(--c-select-padding-h--small);
      --padding-v: var(--c-select-padding-v--small);
      --option-padding-v: var(--c-select-option-padding-v--small);
      --field-height: var(--c-select-field-height--small);
    }

    &--design-design2 {
      --transition: var(--duration-fast) ease-in-out;

      border-radius: var(--c-select-border-radius);

      #{$this}__content {
        @include v2.transition-all(border background color, var(--transition));

        padding: calc(var(--padding-v) - var(--c-select-border-width)) var(--padding-h);
        padding-right: calc(var(--padding-h) + 16px + 10px);
        border: var(--c-select-border-width) solid var(--c-select-border-color);
        border-radius: var(--c-select-border-radius);
        background-color: var(--c-select-background);
      }

      #{$this}__output {
        min-height: var(--field-height);
        padding: 0;
        background: none;
        background-size: functions.rem(16px);
      }

      #{$this}__placeholder,
      #{$this}__value {
        @include v2.font-label--big();

        height: var(--field-height);
        overflow: hidden;
        line-height: var(--field-height);
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      #{$this}__value {
        margin-top: var(--padding-v);
        margin-bottom: calc(var(--padding-v) * -1);
      }

      #{$this}__output-label {
        top: calc(var(--padding-v) * 0.5);
        left: var(--padding-h);
        font-size: 0.8em;
        line-height: 1.4;
      }

      #{$this}__toggle-icon {
        @include v2.transition-all(border background, var(--transition));
        @include v2_fa.fa-icon-solid(v2_fa.$fa-var-angle-down);

        position: absolute;
        top: var(--padding-v);
        right: var(--padding-h);
        color: var(--color-primary--1);
        font-size: 16px;
        line-height: var(--field-height);
      }

      #{$this}__dropdown {
        @include v2.transition-all(border background color, var(--transition));

        border: var(--c-select-border-width) solid var(--c-select-border-color);
        border-radius: calc(var(--c-select-border-radius) - 4px);
        background-color: var(--c-select-background);
        box-shadow: none;
      }

      #{$this}__inner-label {
        @include v2.font-label--big();

        align-items: center;
        min-height: var(--field-height);
        padding: var(--padding-v) var(--padding-h);
      }

      #{$this}__options-wrapper {
        border-radius: 0 0 var(--c-select-border-radius) var(--c-select-border-radius);
      }

      #{$this}__option {
        padding-right: calc(var(--padding-h) + 2ch);
        border-top: 1px solid var(--color-gray--100);
      }

      #{$this}__hint {
        @include v2.font-label--small();
      }

      &#{$this}--active {
        #{$this}__toggle-icon {
          @include v2_fa.fa-icon-solid(v2_fa.$fa-var-angle-up);
          @include v2.z-index(front, 1);

          font-size: 16px;
          line-height: var(--field-height);
        }
      }

      &#{$this}:hover:not(#{$this}--disabled) {
        #{$this}__content {
          border-color: var(--c-select-background--hover);
          background-color: var(--c-select-background--hover);
        }
      }

      &#{$this}--disabled {
        #{$this}__placeholder,
        #{$this}__value,
        #{$this}__output {
          border-color: var(--color-gray--300);
          color: var(--color-gray--300);
        }

        #{$this}__output {
          background-image: none;
        }

        #{$this}__toggle-icon {
          color: var(--color-gray--300);
        }
      }

      &#{$this}--state-error {
        #{$this}__content {
          border-color: var(--c-select-border-color--error);
        }

        #{$this}__notification {
          color: var(--c-select-notification-color--error);
        }
      }

      &#{$this}--state-warning {
        #{$this}__content {
          border-color: var(--c-select-border-color--warning);
        }

        #{$this}__notification {
          color: var(--c-select-notification-color--warning);
        }
      }

      &#{$this}--state-success {
        #{$this}__content {
          border-color: var(--c-select-border-color--success);
        }

        #{$this}__notification {
          color: var(--c-select-notification-color--success);
        }
      }

      &#{$this}--state-info {
        #{$this}__content {
          border-color: var(--c-select-border-color--info);
        }

        #{$this}__notification {
          color: var(--c-select-notification-color--info);
        }
      }

      .e-radio,
      .e-checkbox {
        padding: var(--option-padding-v) var(--padding-h);
      }

      .e-checkbox__checkbox {
        &::before,
        &::after {
          // Restores fake checkbox position after padding was applied.
          padding: 10px var(--padding-h);
        }
      }

      &#{$this}--negative {
        #{$this}__dropdown,
        #{$this}__content {
          border-color: var(--color-gray--100);
          background: var(--color-white);
        }

        &#{$this}:focus-within,
        &#{$this}--focus {
          #{$this}__content {
            border-color: var(--color-gray--200);
          }
        }

        &#{$this}--disabled {
          #{$this}__content {
            border-color: var(--color-gray--100);
          }
        }

        &#{$this}:hover:not(#{$this}--disabled) {
          #{$this}__content {
            border-color: var(--color-gray--300);
            background: var(--color-white);
          }
        }
      }
    }
  }
</style>
